/**
 * Hook that watches for pending redirects from game:finished / game:ended /
 * session:closed events and executes navigation after the specified delay.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore } from '@/stores/gameStore'
import { usePlayerStore } from '@/stores/playerStore'
import { disconnectSocket } from '@/services/socket'

export function useRedirectWatcher() {
  const navigate = useNavigate()
  const sessionCode = usePlayerStore((s) => s.sessionCode)
  const sessionUuid = usePlayerStore((s) => s.sessionUuid)
  const role = usePlayerStore((s) => s.role)
  const redirectAt = useGameStore((s) => s.pendingRedirectAt)
  const redirectDelay = useGameStore((s) => s.pendingRedirectDelay)
  const redirectTarget = useGameStore((s) => s.pendingRedirectTarget)
  const redirectSessionId = useGameStore((s) => s.pendingRedirectSessionId)
  const clearRedirect = useGameStore((s) => s.clearPendingRedirect)

  useEffect(() => {
    if (!redirectAt || redirectDelay === null) return
    // Lobby target needs a session code to build the path; home target does not
    // (the session is gone, so sessionCode may already be missing).
    if (redirectTarget === 'lobby' && !sessionCode) return

    // A redirect that was never consumed (no watcher mounted at the time) must not
    // fire inside a different session joined afterwards.
    if (redirectSessionId !== null && sessionUuid !== null && redirectSessionId !== sessionUuid) {
      clearRedirect()
      return
    }

    const elapsed = Date.now() - redirectAt
    const remaining = Math.max(0, redirectDelay - elapsed)

    const timer = setTimeout(() => {
      clearRedirect()

      if (redirectTarget === 'home') {
        // Session no longer exists. Clear the stores first, so nothing can rebuild a
        // connection to it, then drop the socket still sitting in the closed room.
        usePlayerStore.getState().clear()
        useGameStore.getState().reset()
        disconnectSocket()
        navigate('/')
        return
      }

      const lobbyPath = role === 'tv'
        ? `/session/${sessionCode}/lobby?mode=tv`
        : `/session/${sessionCode}/lobby`
      navigate(lobbyPath)
    }, remaining)

    return () => clearTimeout(timer)
  }, [
    redirectAt,
    redirectDelay,
    redirectTarget,
    redirectSessionId,
    sessionCode,
    sessionUuid,
    role,
    navigate,
    clearRedirect,
  ])
}
